import React, { useCallback, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import type { KpiDrilldownSpec, KpiHeatmapCiApiData, KpiHeatmapCiFocus } from './kpiDashboardApi';
import { SectionLabel } from './KpiDashboardContent';
import KpiCiHeatmapTable, { type CiHeatmapRow } from './KpiCiHeatmapTable';
import KpiIncidentAssignmentGroupComboChart from './charts/KpiIncidentAssignmentGroupComboChart';
import KpiAssigneeAvgCloseChart from './charts/KpiAssigneeAvgCloseChart';
import type { KpiCiTreemapRow } from './charts/KpiCiTreemapChart';
import KpiChartPanel from './charts/KpiChartPanel';
import {
  KPI_DASHBOARD_TABLE_ROW_SELECTED,
  KPI_DASHBOARD_TABLE_TD,
  KPI_DASHBOARD_TABLE_TD_NUM,
  KPI_DASHBOARD_TABLE_TH,
  KPI_DASHBOARD_TABLE_TH_CENTER,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';

const UNIFORM_BAR = 'rgb(47, 85, 151)';
const UNIFORM_BAR_SELECTED = 'rgb(30, 58, 120)';

function ciLabelsEqualCaseInsensitive(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}

function heatmapRowMatchesFocus(row: CiHeatmapRow, f: KpiHeatmapCiFocus): boolean {
  if (!f.label && !f.resourceId) return false;
  if (f.resourceId && row.ciResourceId) return f.resourceId === row.ciResourceId;
  return ciLabelsEqualCaseInsensitive(f.label, row.ciLabel);
}

export interface KpiHeatmapCiTabProps {
  data: KpiHeatmapCiApiData | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  onRetry?: () => void;
  onDrill: (spec: KpiDrilldownSpec) => void;
  focus: KpiHeatmapCiFocus;
  onFocusChange: (next: KpiHeatmapCiFocus) => void;
}

const emptyFocus = (): KpiHeatmapCiFocus => ({
  resourceId: null,
  label: null,
  weekStart: null,
  assignmentGroupName: null,
  assigneeUserId: null,
});

const KpiHeatmapCiTab: React.FC<KpiHeatmapCiTabProps> = ({
  data,
  isLoading,
  isFetching,
  error,
  onRetry,
  onDrill,
  focus,
  onFocusChange,
}) => {
  const [ciSortDesc, setCiSortDesc] = useState(false);
  const [topCiSort, setTopCiSort] = useState<{ by: 'count' | 'label'; desc: boolean }>({
    by: 'count',
    desc: true,
  });

  const sortedRows = useMemo(() => {
    if (!data?.heatmap.rows.length) return [];
    const rows = [...data.heatmap.rows];
    rows.sort((a, b) => {
      const cmp = a.ciLabel.localeCompare(b.ciLabel, undefined, { sensitivity: 'base' });
      return ciSortDesc ? -cmp : cmp;
    });
    return rows;
  }, [data?.heatmap.rows, ciSortDesc]);

  const treemapRows: KpiCiTreemapRow[] = useMemo(
    () =>
      (data?.ciTreemap ?? []).map((r) => ({
        treemapKey: r.ciResourceId ?? `lbl:${r.ciLabel.trim().toLowerCase()}`,
        ciLabel: r.ciLabel,
        count: r.count,
      })),
    [data?.ciTreemap]
  );

  const topCiRows = useMemo(() => {
    const rows = [...treemapRows];
    rows.sort((a, b) => {
      const base =
        topCiSort.by === 'count'
          ? b.count - a.count || a.ciLabel.localeCompare(b.ciLabel, undefined, { sensitivity: 'base' })
          : a.ciLabel.localeCompare(b.ciLabel, undefined, { sensitivity: 'base' }) || b.count - a.count;
      return topCiSort.desc ? base : -base;
    });
    return rows.slice(0, 10);
  }, [treemapRows, topCiSort]);

  const toggleTopCiSort = useCallback((by: 'count' | 'label') => {
    setTopCiSort((prev) => {
      if (prev.by === by) return { ...prev, desc: !prev.desc };
      return { by, desc: by === 'count' };
    });
  }, []);

  const handleToggleCi = useCallback(
    (row: CiHeatmapRow) => {
      if (heatmapRowMatchesFocus(row, focus)) {
        onFocusChange({ ...focus, resourceId: null, label: null });
      } else {
        onFocusChange({ ...focus, resourceId: row.ciResourceId, label: row.ciLabel });
      }
    },
    [focus, onFocusChange]
  );

  const handleToggleWeek = useCallback(
    (weekStart: string) => {
      if (focus.weekStart === weekStart) {
        onFocusChange({ ...focus, weekStart: null });
      } else {
        onFocusChange({ ...focus, weekStart });
      }
    },
    [focus, onFocusChange]
  );

  const handleToggleCell = useCallback(
    (row: CiHeatmapRow, weekStart: string) => {
      const sameCell = heatmapRowMatchesFocus(row, focus) && focus.weekStart === weekStart;
      if (sameCell) {
        onFocusChange(emptyFocus());
      } else {
        onFocusChange({
          resourceId: row.ciResourceId,
          label: row.ciLabel,
          weekStart,
          assignmentGroupName: null,
          assigneeUserId: null,
        });
      }
    },
    [focus, onFocusChange]
  );

  const handleToggleAssignmentGroup = useCallback(
    (groupName: string) => {
      if (focus.assignmentGroupName === groupName) {
        onFocusChange({ ...focus, assignmentGroupName: null });
      } else {
        onFocusChange({ ...focus, assignmentGroupName: groupName });
      }
    },
    [focus, onFocusChange]
  );

  const handleAssignmentGroupCountDrill = useCallback(
    (groupName: string) => {
      onDrill({
        kind: 'ci_backlog_assignment_group',
        groupName,
        ...(focus.resourceId ? { ciResourceId: focus.resourceId } : {}),
        ...(focus.label ? { ciLabel: focus.label } : {}),
        ...(focus.weekStart ? { weekStart: focus.weekStart } : {}),
      });
    },
    [focus.label, focus.resourceId, focus.weekStart, onDrill]
  );

  const handleAssigneeCountDrill = useCallback(
    (userId: string) => {
      const displayName = data?.assigneeOpenBacklog.find((r) => r.userId === userId)?.displayName;
      onDrill({
        kind: 'ci_backlog_assignee',
        userId,
        displayName,
        ...(focus.resourceId ? { ciResourceId: focus.resourceId } : {}),
        ...(focus.label ? { ciLabel: focus.label } : {}),
        ...(focus.weekStart ? { weekStart: focus.weekStart } : {}),
      });
    },
    [data?.assigneeOpenBacklog, focus.label, focus.resourceId, focus.weekStart, onDrill]
  );

  const handleToggleAssignee = useCallback(
    (userId: string) => {
      if (focus.assigneeUserId === userId) {
        onFocusChange({ ...focus, assigneeUserId: null });
      } else {
        onFocusChange({ ...focus, assigneeUserId: userId });
      }
    },
    [focus, onFocusChange]
  );

  const handleTreemapToggle = useCallback(
    (row: KpiCiTreemapRow) => {
      const rid = row.treemapKey.startsWith('lbl:') ? null : row.treemapKey;
      const sameCi =
        (rid != null && focus.resourceId === rid) ||
        (rid == null && focus.resourceId == null && ciLabelsEqualCaseInsensitive(focus.label, row.ciLabel));
      if (sameCi) {
        onFocusChange({ ...focus, resourceId: null, label: null });
      } else {
        onFocusChange({ ...focus, resourceId: rid, label: row.ciLabel });
      }
    },
    [focus, onFocusChange]
  );

  const handleTreemapDrill = useCallback(
    (row: KpiCiTreemapRow) => {
      const rid = row.treemapKey.startsWith('lbl:') ? null : row.treemapKey;
      onDrill({
        kind: 'heatmap_ci_volume',
        ciResourceId: rid,
        ciLabel: row.ciLabel,
      });
    },
    [onDrill]
  );

  const clearFocus = useCallback(() => {
    onFocusChange(emptyFocus());
  }, [onFocusChange]);

  if (isLoading && !data) {
    return (
      <div className="space-y-8" aria-busy="true" aria-label="Loading heatmap CI">
        <div>
          <div className="mb-3 h-3 w-48 animate-pulse rounded bg-slate-200" />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="h-64 animate-pulse rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-100 to-slate-50 shadow-sm lg:min-h-[22rem]" />
            <div className="h-64 animate-pulse rounded-xl border border-slate-200/80 bg-gradient-to-br from-slate-100 to-slate-50 shadow-sm lg:min-h-[22rem]" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
          <div className="h-72 animate-pulse rounded-xl border border-slate-200/80 bg-slate-100/80 shadow-sm" />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
        <p className="mt-3 text-sm font-semibold text-red-900">Could not load heatmap (CI) data</p>
        <p className="mt-1 text-sm text-red-700/90">{error.message}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!data) return null;

  const hasFocus = Boolean(
    focus.label || focus.resourceId || focus.weekStart || focus.assignmentGroupName || focus.assigneeUserId
  );

  const tableFocus =
    hasFocus
      ? {
          ciResourceId: focus.resourceId,
          ciLabel: focus.label,
          weekStart: focus.weekStart,
        }
      : null;

  return (
    <div className="relative">
      {isFetching && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
          Updating
        </div>
      )}
      {error && data && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">
            Showing cached data. Latest refresh failed: {error.message}
          </span>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
            >
              Retry
            </button>
          )}
        </div>
      )}

      <section className="mb-8">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <SectionLabel>Tickets by configuration item</SectionLabel>
          {hasFocus && (
            <button
              type="button"
              onClick={clearFocus}
              className="text-xs font-semibold text-slate-600 underline decoration-slate-400 underline-offset-2 hover:text-slate-900"
            >
              Clear slice
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2 lg:gap-5">
          <div className="min-h-0 lg:col-span-1">
            <KpiCiHeatmapTable
              title="Tickets Volume By CI"
              heatmap={data.heatmap}
              sortedRows={sortedRows}
              onToggleCiSort={() => setCiSortDesc((d) => !d)}
              maxCount={data.heatmap.maxCount > 0 ? data.heatmap.maxCount : 1}
              focus={tableFocus}
              onToggleCi={handleToggleCi}
              onToggleWeek={handleToggleWeek}
              onToggleCell={handleToggleCell}
              onCellDrill={onDrill}
            />
          </div>
          <div className="min-h-0 lg:col-span-1">
            <KpiChartPanel
              title="Top 10 CI"
              flexFill
              className="min-h-0"
              bodyClassName="flex min-h-0 flex-1 flex-col px-0 pb-0 pt-0"
            >
              {/* <div className="flex items-center justify-end gap-2 px-4 py-2 text-[11px] font-medium text-slate-500">
                Click to filter · Double-click to drill
              </div> */}
              <div className="max-h-[min(28rem,70vh)] min-h-0 overflow-auto">
                <table className="w-max min-w-full border-collapse text-left text-xs">
                  <thead>
                    <tr>
                      <th className={KPI_DASHBOARD_TABLE_TH} scope="col">
                        <button
                          type="button"
                          onClick={() => toggleTopCiSort('label')}
                          className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left font-semibold text-slate-800 hover:bg-sky-200/50"
                        >
                          Configuration Item
                          <ArrowsUpDownIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden />
                        </button>
                      </th>
                      <th className={`${KPI_DASHBOARD_TABLE_TH_CENTER} w-28`} scope="col">
                        <button
                          type="button"
                          onClick={() => toggleTopCiSort('count')}
                          className="inline-flex items-center justify-center gap-1.5 rounded-md px-1 py-0.5 font-semibold text-slate-800 hover:bg-sky-200/50"
                        >
                          Tickets
                          <ArrowsUpDownIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden />
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCiRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={2}
                          className="border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500"
                        >
                          No configuration items in this period for the selected filters.
                        </td>
                      </tr>
                    ) : (
                      topCiRows.map((r, i) => {
                        const selected =
                          (focus.resourceId &&
                            !r.treemapKey.startsWith('lbl:') &&
                            focus.resourceId === r.treemapKey) ||
                          (!focus.resourceId &&
                            focus.label &&
                            r.treemapKey.startsWith('lbl:') &&
                            ciLabelsEqualCaseInsensitive(focus.label, r.ciLabel));
                        return (
                          <tr
                            key={r.treemapKey}
                            className={`${kpiDashboardTableRowClass(i)} cursor-pointer`}
                            onClick={() => handleTreemapToggle(r)}
                            onDoubleClick={() => handleTreemapDrill(r)}
                          >
                            <td
                              className={`${KPI_DASHBOARD_TABLE_TD} px-3 py-2 text-left font-medium text-slate-900 ${
                                selected ? KPI_DASHBOARD_TABLE_ROW_SELECTED : ''
                              }`}
                            >
                              <span className="line-clamp-2">{r.ciLabel}</span>
                            </td>
                            <td
                              className={`${KPI_DASHBOARD_TABLE_TD_NUM} px-3 py-2 text-center font-semibold ${
                                selected ? KPI_DASHBOARD_TABLE_ROW_SELECTED : ''
                              }`}
                            >
                              {r.count.toLocaleString()}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </KpiChartPanel>
          </div>
        </div>
      </section>

      <section>
        <SectionLabel>Backlog &amp; assignees</SectionLabel>
        <div className="mt-3 grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2 lg:gap-5">
          <KpiIncidentAssignmentGroupComboChart
            rows={data.backlogByAssignmentGroup}
            panelTitle="Tickets Backlog By Assignment Group w/ SLA Compliance %"
            emptyMessage="No open Incident or Request backlog with a CI in this slice."
            slaLineMode="compliance"
            barDatasetLabel="Ticket count"
            uniformBarColor={UNIFORM_BAR}
            selectedGroupName={focus.assignmentGroupName}
            selectedBarColor={UNIFORM_BAR_SELECTED}
            onGroupClick={handleToggleAssignmentGroup}
            onGroupCountClick={handleAssignmentGroupCountDrill}
          />
          <KpiAssigneeAvgCloseChart
            panelTitle="Open Tickets By Assignee w/ Avg Days Open"
            emptyMessage="No open Incident or Request backlog with a CI in this slice (assigned tickets only)."
            lineDatasetLabel="Avg days open"
            rows={data.assigneeOpenBacklog.map((r) => ({
              userId: r.userId,
              displayName: r.displayName,
              count: r.count,
              avgClosedDays: r.avgOpenDays,
            }))}
            selectedUserId={focus.assigneeUserId}
            selectedBarColor={UNIFORM_BAR_SELECTED}
            onAssigneeClick={handleToggleAssignee}
            onAssigneeCountClick={handleAssigneeCountDrill}
          />
        </div>
      </section>
    </div>
  );
};

export default KpiHeatmapCiTab;
